<template>
  <div>
    <h6 class="font-italic mt-1 mb-4">
      {{ $t('reports_ui.route_assessment_metrics_report.two_day_delay_note') }}
    </h6>
    <v-form>
      <v-text-field
        v-if="localReport.id"
        v-model="localReport.name"
        :label="$tc('name')"
        :error-messages="nameErrors"
        prepend-inner-icon="mdi-form-textbox"/>
      <vf-autocomplete
        :label="$tc('customer', 2)"
        v-model="reportCustomers"
        :items="availableCustomers"
        return-object
        all/>
      <div>
        <div class="d-flex align-center">
          <date-picker
            v-model="startDate"
            :max="maxDate"
            :timezone="timezone"
            :output-moment="true"
            :label="$t('start_date')"
            prepend-inner-icon="mdi-calendar"
            :error-messages="dateRangeErrors"
            :reset-on-timezone-change="context === 'create'"/>
          <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
          <date-picker
            v-model="endDate"
            :max="maxDate"
            :timezone="timezone"
            :output-moment="true"
            :end-of-day="true"
            :label="$t('end_date')"
            prepend-inner-icon="mdi-calendar"
            :error="dateRangeErrors.length > 0"
            :reset-on-timezone-change="context === 'create'"/>
        </div>
        <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone"/>
      </div>
      <v-row>
        <v-col>
          <v-checkbox
            v-model="include_deleted"
            :label="$t('reports_ui.rca_report.include_deleted')"/>
        </v-col>
        <v-col>
          <v-checkbox
            v-model="assessed"
            :label="$t('reports_ui.rca_report.assessed')"/>
        </v-col>
        <v-col>
          <v-checkbox
            v-model="unassessed"
            :label="$t('reports_ui.rca_report.unassessed')"/>
        </v-col>
        <v-col>
          <v-radio-group v-model="assessedTimeframe" row v-if="assessed">
            <v-radio
              :label="$t('reports_ui.rca_report.assessed_late')"
              value="late"/>
            <v-radio
              :label="$t('reports_ui.rca_report.assessed_within')"
              value="within"/>
          </v-radio-group>
          <v-select
            v-if="assessedTimeframe === 'within'"
            :label="$t('reports_ui.rca_report.timeframe')"
            v-model="assessedWithin"
            :items="assessedWithinOptions" />
        </v-col>
      </v-row>

    </v-form>
  </div>

</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import DatePicker from '@/components/shared/DatePicker'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'
import VfAutocomplete from '@/components/shared/VfAutocomplete'

import Moment from 'moment-timezone'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { get, cloneDeep, forEach, find, map } from 'lodash'

export default {
  name: 'RouteAssessmentMetricsReportForm',
  components: {
    DatePicker,
    DateSelectorShortcuts,
    VfAutocomplete
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    ValidationMixin,
    ReportFormMixin
  ],
  validations: {
    reportCustomers: {
      required
    },
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.localReport.id != null
        })
      }
    },
    dateRange: {
      rangeIsValid: function (value) {
        return this.startDate != null &&
          this.endDate != null &&
          this.startDate.isValid() &&
          this.endDate.isValid() &&
          this.endDate >= this.startDate
      }
    }
  },
  props: {
    report: {
      type: Object
    },
    context: {
      type: String,
      default: 'create'
    }
  },
  data () {
    return {
      reportType: 'RCAReport',
      localReport: cloneDeep(this.report),
      action: this.context,
      startDate: null,
      endDate: null,
      reportPeriod: null,
      reportCustomers: [],
      saving: false,
      assessedWithin: 100000,
      assessedWithinOptions: [
        { text: 'No Limit', value: 100000 },
        { text: '1 Day', value: 1 },
        { text: '2 Days', value: 2 },
        { text: '3 Days', value: 3 },
        { text: '4 Days', value: 4 },
        { text: '5 Days', value: 5 },
        { text: '6 Days', value: 6 },
        { text: '7 Days', value: 7 }
      ],
      assessed: true,
      unassessed: false,
      include_deleted: false,
      assessedTimeframe: 'within'
    }
  },
  computed: {
    /**
     * Get the timezone that the report is operating under. If a new report,
     * default to the current customer context's timezone.
     */
    timezone () {
      return get(this.report, 'parameters.timezone', this.customerContext.timezone())
    },
    /**
     * Lists customers available to the current user (customers in their scope)
     */
    availableCustomers () {
      return map(this.customers, customer => {
        return {
          value: customer.id,
          text: customer.name
        }
      })
    },
    /**
     * The date picker options used in the 'monthly' LegacyDatePicker
     */
    monthlyDatePickerOptions () {
      return {
        format: 'MM/YYYY',
        viewMode: 'months',
        locale: this.$i18n.locale,
        useCurrent: false,
        maxDate: Moment().tz(this.timezone).startOf('month')
      }
    },
    /**
     * The maximum date selectable in the date pickers.
     */
    maxDate () {
      return Moment.tz(this.timezone).subtract(2, 'days').toISOString()
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localReport.name,
        message: this.$t('required')
      }])
    },
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    }
  },
  methods: {
    populateReportForm () {
      this.preSelectDates()
      this.preSelectCustomers()
      this.preSelectReportParams()
    },
    buildReportParameters () {
      const reportParameters = {}

      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      reportParameters.customerIds = map(this.reportCustomers, x => x.value)
      reportParameters.timezone = this.timezone
      reportParameters.assessed = this.assessed
      reportParameters.unassessed = this.unassessed
      reportParameters.includeDeleted = this.include_deleted
      reportParameters.assessedTimeframe = this.assessedTimeframe
      reportParameters.assessedWithin = this.assessedWithin

      return reportParameters
    },
    defaultReportName () {
      return this.$t('reports_ui.rca_report.name')
    },
    /**
     * Pull the date values selected from a previous report (if defined) and set the
     * state of this form to match
     *
     * @var ReportRecord report
     * @return undefined
     */
    preSelectDates () {
      const preSelectedStartDate = get(this, 'report.parameters.startDate')
      if (preSelectedStartDate) {
        this.startDate = Moment.tz(preSelectedStartDate, this.timezone)
      }
      const preSelectedEndDate = get(this, 'report.parameters.endDate')
      if (preSelectedEndDate) {
        this.endDate = Moment.tz(preSelectedEndDate, this.timezone)
      }
    },
    preSelectReportParams () {
      const preSelectedAssessed = get(this, 'report.parameters.assessed')
      if (preSelectedAssessed) {
        this.assessed = preSelectedAssessed
      }
      const preSelectedUnassessed = get(this, 'report.parameters.unassessed')
      if (preSelectedUnassessed) {
        this.unassessed = preSelectedUnassessed
      }
      const preSelectedIncludeDeleted = get(this, 'report.parameters.includeDeleted')
      if (preSelectedIncludeDeleted) {
        this.include_deleted = preSelectedIncludeDeleted
      }
      const preSelectedAssessedTimeframe = get(this, 'report.parameters.assessedTimeframe')
      if (preSelectedAssessedTimeframe) {
        this.assessedTimeframe = preSelectedAssessedTimeframe
      }
      const preSelectedAssessedWithin = get(this, 'report.parameters.assessedWithin')
      if (preSelectedAssessedWithin) {
        this.assessedWithin = preSelectedAssessedWithin
      }
    }
  }
}
</script>

<style scoped>

</style>
